import { useCommonUserRetrieveSuspense } from "@boundless/bl-api-client/common/services"
import { useCustomerCompanyRetrieveSuspense } from "@boundless/bl-api-client/customer/services"
import { CircleCheckBigIcon, CircleXIcon } from "lucide-react"
import { useStore } from "zustand"

import { useDocuments } from "@/components/documents/context/DocumentsProvider"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { store } from "@/lib/store"
import { cn } from "@/lib/utils"

export function RequestedDocumentsCard({
  companyUuid,
  className,
}: {
  companyUuid: string
  className?: string
}) {
  const { savedTypeIds } = useDocuments()
  const previouslyClosed = useStore(
    store,
    (state) => state.requestDocumentsCardClosed,
  )

  const { data: company } = useCustomerCompanyRetrieveSuspense(companyUuid)
  const { data: user } = useCommonUserRetrieveSuspense()

  const handleClose = () => {
    store.setState({ requestDocumentsCardClosed: true })
  }

  const isConfirmed = (id: number) => savedTypeIds.includes(id)
  const allIncluded = company.requestedDocuments.every((doc) =>
    savedTypeIds.includes(doc.id),
  )

  const hideCard = (() => {
    if (!company.requestedDocuments.length) {
      return true
    }

    if (user.isImpersonated) {
      return false
    }

    return allIncluded && previouslyClosed
  })()

  if (hideCard) {
    return null
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Requested Documents</CardTitle>
        <CardDescription>
          The following documents have been requested:
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <ul className="flex flex-col gap-1">
          {company.requestedDocuments.map((doc) => {
            return (
              <li className="flex flex-row items-center gap-2" key={doc.id}>
                {isConfirmed(doc.id) ? (
                  <CircleCheckBigIcon className="text-success h-4 w-4" />
                ) : (
                  <CircleXIcon className="text-error h-4 w-4" />
                )}
                <span
                  className={cn("text-xs", isConfirmed(doc.id) && "text-muted")}
                >
                  {doc.name}
                </span>
              </li>
            )
          })}
        </ul>
        {allIncluded && !user.isImpersonated && (
          <div className="flex flex-col gap-5">
            <p className="text-sm">All requested documents uploaded!</p>
            <Button onClick={handleClose}>Close Tab</Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
